import React, { useState, useEffect, useMemo } from "react";
import { useLibraryCreativesContext } from "../../../../core/components/creatives/library/useLibraryCreativesContext";
import { determineUserAccess } from "../../../../core/permissions/userLevelAccess";
import { message, Tabs, Select, Empty, Modal, Form, Input, Button } from "antd";
import { AppstoreAddOutlined, DownOutlined } from "@ant-design/icons";
import AppSpinner from "../../Tasks/components/shared/AppSpinner";
import { errorType } from "../../../../core/utils/constants/constants";
import { useAssignAdUnits } from "../hooks/assignAdUnits";

// Navigation hooks
import { useCreativeNavigation } from "../hooks/useCreativeNavigation";
import { usePaginationState } from "../hooks/usePaginationState";
import { useRouteValidation } from "../hooks/useRouteValidation";
import { useMediaQuery } from "../hooks/useMediaQuery";

// Components
import CreativeToolbar from "../components/CreativeToolbar";
import CreativeGridCard from "../components/CreativeGridCard";
import CreativeListRow from "../components/CreativeListRow";
import GroupGridCard from "../components/GroupGridCard";
import GroupListRow from "../components/GroupListRow";
import SelectionAlertRow from "../components/SelectionAlertRow";
import SkeletonGrid from "../components/SkeletonGrid";
import CreativeTable from "../components/CreativeTable";
import CreativePagination from "../components/CreativePagination";

// Existing components (kept for modals / business logic)
import MediaUploadModal from "../components/MediaUploadModal";
import AdUnitAssignmentModal from "../components/AdUnitAssignmentModal";
import { CreativeAdGroupForm } from "../../OrgConfig/components/CreativeAdGroupForm";
import { CreativeAdGroupAddCreativesModal } from "../../OrgConfig/components/CreativeAdGroupAddCreativesModal";
import { uploadToS3Storage } from "../utils/mediaUtils";
import { multiImageZipKey, multiGroupZipKey } from "../../../../core/utils/dataloading/multiImageZipKey";

// Styles
import "../styles/_creatives.scss";

// Governed pagination tokens
import { pagination as paginationTokens } from "../../Tasks/configs/designTokens";
const DEFAULT_PAGE_SIZE = paginationTokens.defaultPageSize;

// ─── Sorting helpers ───────────────────────────────────────────────────────
const sortItems = (items, sortOption, sortDirection) => {
  if (!items || items.length === 0) return [];
  const sorted = [...items].sort((a, b) => {
    let valA, valB;
    switch (sortOption) {
      case "name":
        valA = (a.name || "").toLowerCase();
        valB = (b.name || "").toLowerCase();
        return valA.localeCompare(valB);
      case "createdAt":
        valA = new Date(a.createdAt || a.updatedAt || 0).getTime();
        valB = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return valA - valB;
      case "fileSize":
        valA = a.size || 0;
        valB = b.size || 0;
        return valA - valB;
      default:
        return 0;
    }
  });
  return sortDirection === "desc" ? sorted.reverse() : sorted;
};

const filterItems = (items, searchText) => {
  if (!searchText) return items || [];
  const q = searchText.toLowerCase();
  return (items || []).filter((item) => {
    const name = (item.name || "").toLowerCase();
    const fileType = (item.fileType || "").toLowerCase();
    return name.includes(q) || fileType.includes(q);
  });
};

// ─── Component ─────────────────────────────────────────────────────────────
const LibraryCreativesView = () => {
  document.title = "Creatives Main View";

  // ── Navigation ──────────────────────────────────────────────────────────
  const {
    currentState,
    navigateToTab,
    navigateToPage,
    navigateToDetail,
    getNavigationHelpers,
  } = useCreativeNavigation();

  const { updateCurrentPagination } = usePaginationState();

  const { isRouteValid } = useRouteValidation({
    autoRedirect: true,
    onInvalidRoute: (errors) => {
      console.warn("Redirecting from invalid creative route:", errors);
      message.warning("Invalid page parameters, redirecting to default view");
    },
  });

  // ── Context ─────────────────────────────────────────────────────────────
  const {
    loggedInOrg,
    updateManyMedias,
    getAdGroupsByMedia,
    deleteAdGroupCreatives,
    currentuser,
    createAdGroupCreatives,
    adGroupCreatives,
    orgMedias,
    refetchMedias,
    updateOrgMedia,
    fetchVideoMedias,
    refetchVideoMedias,
    orgVideoMedias,
    updateManyAdGroupCreatives,
    updateAdGroupCreatives,
    createManyOrgMedia,
    createOrgMedia,
    setDefaultAmplifyCreatives,
    handleRowClick,
  } = useLibraryCreativesContext() || {};

  const helpers = getNavigationHelpers();

  // ── Permissions ─────────────────────────────────────────────────────────
  const { permission } = currentuser || {};
  const userPermission = permission ? determineUserAccess(permission) : {};
  const { amplify } = userPermission || {};
  const { handleAssignAdUnits, assigning } = useAssignAdUnits();

  // ── Responsive ─────────────────────────────────────────────────────────
  // Governed breakpoints — match _task-tokens.scss
  // $breakpoint-xl: 1200px (desktop table view)
  // $breakpoint-sm: 576px (mobile dropdown)
  const isDesktop = useMediaQuery("(min-width: 1200px)");
  const isMobile = useMediaQuery("(max-width: 576px)");

  // ── Local state ─────────────────────────────────────────────────────────
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [sortOption, setSortOption] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Selection state — unified across tabs
  const [selectedIds, setSelectedIds] = useState([]);

  // Upload modal
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadedMedia, setUploadedMedia] = useState(null);

  // Assign modal
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [selectedAdGroupIds, setSelectedAdGroupIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);

  // Create group modal
  const [createGroupVisible, setCreateGroupVisible] = useState(false);
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [createGroupForm] = Form.useForm();
  const [adGroupMediasState, setAdGroupMediasState] = useState([]);

  // Edit group / Add creatives modals
  const [editGroupVisible, setEditGroupVisible] = useState(false);
  const [addCreativesVisible, setAddCreativesVisible] = useState(false);
  const [selectedGroupRow, setSelectedGroupRow] = useState(null);
  const [groupCreativeKeys, setGroupCreativeKeys] = useState([]);

  // Loading flags
  const [onLoadingDeleteMedias, setOnLoadingDeleteMedias] = useState(false);
  const [onLoadingDeleteAdGroup, setOnLoadingDeleteAdGroup] = useState(false);

  // ── Effects ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setSelectedOrg({ ...loggedInOrg });
  }, [loggedInOrg]);

  // Clear selection when switching tabs
  useEffect(() => {
    setSelectedIds([]);
    setSearchText("");
  }, [currentState.tab]);

  // Lazy-load video data when a video tab is first visited
  useEffect(() => {
    if (helpers.isVideoAds && !orgVideoMedias && fetchVideoMedias) {
      fetchVideoMedias();
    }
  }, [helpers.isVideoAds, orgVideoMedias, fetchVideoMedias]);

  // ── Data derivation ─────────────────────────────────────────────────────
  // orgMedias = array | undefined (pre-extracted in provider; undefined = still loading)
  // orgVideoMedias = { medias: [...] } | undefined (raw query result)
  const videoMediasArray = orgVideoMedias?.medias || [];

  // Detect loading: data is undefined when queries haven't resolved
  const isDataLoading = useMemo(() => {
    if (helpers.isAdUnits) {
      return helpers.isDisplayAds ? orgMedias === undefined : orgVideoMedias === undefined;
    }
    // Groups share the adGroupCreatives query
    return adGroupCreatives === undefined;
  }, [helpers.isAdUnits, helpers.isDisplayAds, orgMedias, orgVideoMedias, adGroupCreatives]);

  const rawUnits = useMemo(() => {
    if (helpers.isDisplayAds) return orgMedias || [];
    if (helpers.isVideoAds) return videoMediasArray;
    return [];
  }, [helpers.isDisplayAds, helpers.isVideoAds, orgMedias, videoMediasArray]);

  const rawGroups = useMemo(() => {
    const all = adGroupCreatives || [];
    return all
      .filter((group) => Array.isArray(group.medias) && group.medias.length > 0)
      .filter((group) => {
        if (helpers.isDisplayAds) {
          // Display groups: exclude groups that contain MP4/video media
          return !group.medias.some((m) => m.fileType === "MP4");
        }
        // Video groups: only groups that contain MP4/video media
        return group.medias.some((m) => m.fileType === "MP4");
      });
  }, [adGroupCreatives, helpers.isDisplayAds]);

  // Apply filter + sort
  const processedItems = useMemo(() => {
    const items = helpers.isAdUnits ? rawUnits : rawGroups;
    const filtered = filterItems(items, searchText);
    return sortItems(filtered, sortOption, sortDirection);
  }, [helpers.isAdUnits, rawUnits, rawGroups, searchText, sortOption, sortDirection]);

  // Paginate
  const pageNumber = currentState.pageNumber || 1;
  const totalItems = processedItems.length;
  const paginatedItems = useMemo(() => {
    const start = (pageNumber - 1) * pageSize;
    return processedItems.slice(start, start + pageSize);
  }, [processedItems, pageNumber, pageSize]);

  // Selected items (full objects, for assignment modal)
  const selectedUnits = useMemo(() => {
    return rawUnits.filter((m) => selectedIds.includes(m.id));
  }, [rawUnits, selectedIds]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleTabChange = (tabKey) => {
    navigateToTab(tabKey);
  };

  const handlePageChange = (page) => {
    updateCurrentPagination(page);
    navigateToPage(page);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    handlePageChange(1);
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // ── Business logic (unchanged) ──────────────────────────────────────────

  const onDeleteMedias = async (ids) => {
    try {
      message.loading("Action in progress..", 0);
      setOnLoadingDeleteMedias(true);

      await updateManyMedias({
        variables: { ids, data: { flag: false } },
      });

      message.destroy();
      message.success(
        `${ids.length} creative${ids.length === 1 ? "" : "s"} successfully deleted.`
      );
      setOnLoadingDeleteMedias(false);

      const adGroupCreativesResult = await getAdGroupsByMedia({
        media: ids,
        flag: true,
      });

      const mappedAdGroupCreativeIds = (
        adGroupCreativesResult?.data?.adGroupCreativeses || []
      )
        .filter(
          (obj) =>
            obj?.medias &&
            Array.isArray(obj.medias) &&
            obj.medias.every((media) => !media.flag)
        )
        .map((obj) => obj.id);

      if (mappedAdGroupCreativeIds.length > 0) {
        mappedAdGroupCreativeIds.forEach((x) =>
          deleteAdGroupCreatives({ variables: { id: x } })
        );
      }

      setSelectedIds([]);
      return ids;
    } catch (err) {
      message.destroy();
      setOnLoadingDeleteMedias(false);
      if (err.message === errorType.deleteMedia) {
        message.error(
          "Cannot delete campaign - it is already synced with a creative."
        );
      } else {
        message.error("Failed to delete creatives. Please try again.");
      }
      throw err;
    }
  };

  const onDeleteAdgroup = async (ids) => {
    try {
      message.loading("Action in progress..", 0);
      setOnLoadingDeleteAdGroup(true);

      const results = ids.map((id) =>
        deleteAdGroupCreatives({ variables: { id } })
      );
      const values = await Promise.all(results);

      message.destroy();
      message.success(
        `${values.length} ad group${values.length === 1 ? "" : "s"} successfully deleted.`
      );
      setOnLoadingDeleteAdGroup(false);
      setSelectedIds([]);
    } catch (err) {
      message.destroy();
      message.error("Failed to delete ad groups.");
    }
  };

  const setAdGroupAsAmplifyDefault = async (record) => {
    await updateManyAdGroupCreatives({
      variables: { orgId: record.org[0].id, isAmplifyDefault: false },
    });
    await updateAdGroupCreatives({
      variables: { id: record.id, data: { isAmplifyDefault: true } },
    });
  };

  const updateAdGroup = async ({ adGroup, type }) => {
    try {
      const { id, name, medias } = adGroup;
      await updateAdGroupCreatives({
        variables: {
          id,
          data: {
            name,
            medias: { [type]: medias.map((mid) => ({ id: mid })) },
          },
        },
      });
    } catch (error) {
      console.error("Error updating ad group:", error);
      throw error;
    }
  };

  const onCreateAdgroup = async (customName = null, customMedias = null) => {
    const {
      role,
      permission: { amplify: ampPerm },
    } = currentuser;

    const orgs = [role.org.id];
    const groupName = customName;
    const groupMedias = customMedias || adGroupMediasState;

    try {
      message.loading("Action in progress..", 0);

      const result = await createAdGroupCreatives({
        variables: {
          name: groupName,
          medias: groupMedias,
          org: orgs.map((o) => ({ id: o })),
        },
      });

      const hasExistingAmplifyDefault =
        adGroupCreatives &&
        adGroupCreatives.some((ag) => ag.isAmplifyDefault);
      if (ampPerm && !hasExistingAmplifyDefault) {
        const createdAdGroup = result?.data?.createAdGroupCreatives;
        if (createdAdGroup) {
          setAdGroupAsAmplifyDefault(createdAdGroup);
        }
      }

      message.destroy();
      message.success("Successfully Created Ad Group");

      setSelectedIds([]);
      setAdGroupMediasState([]);

      refetchMedias().catch((err) =>
        console.warn("Failed to refresh media data:", err)
      );
    } catch (err) {
      message.destroy();
      message.error("Failed to create ad group. Please try again.");
    }
  };

  const bulkCreateOrgMedia = async (medias, invalidMedias) => {
    try {
      if (!selectedOrg || !selectedOrg.id) {
        message.error("No organization selected");
        return;
      }

      message.loading("Uploading creatives...", 0);

      const chunkSize = 10;
      const totalChunks = Math.ceil(medias.length / chunkSize);

      for (let i = 0; i < totalChunks; i++) {
        const chunkMedias = medias.slice(i * chunkSize, (i + 1) * chunkSize);
        const result = await createManyOrgMedia({
          variables: {
            id: selectedOrg.id,
            data: { media: { create: chunkMedias } },
          },
        });
        if (!result?.data) throw new Error("Failed to upload media");
      }

      message.destroy();
      if (invalidMedias.length) {
        message.error(
          `Failed to upload ${invalidMedias.length} creative(s).`
        );
      }
      message.success(`${medias.length} Creative(s) successfully uploaded`);
    } catch (error) {
      message.destroy();
      message.error(
        `Failed to upload creatives: ${error.message || "Unknown error"}`
      );
      console.error("Bulk upload error:", error);
      throw error;
    }
  };

  // Upload handler for MediaUploadModal — handles both single and bulk modes
  const handleMediaUpload = async (mediaData, mode) => {
    const mediaTypeValue = helpers.isVideoAds ? "VIDEO" : "STILL";
    setUploadLoading(true);

    try {
      if (mode === "single") {
        const media = mediaData[0];
        const result = await createOrgMedia({
          variables: {
            mediatype: mediaTypeValue,
            key: media.key,
            url: media.url,
            width: media.width,
            height: media.height,
            name: media.name,
            size: media.size,
            fileType: media.fileType,
          },
        });
        if (helpers.isVideoAds) {
          await refetchVideoMedias();
        } else {
          await refetchMedias();
        }
        const created = result?.data?.createMedia;
        setUploadedMedia(created ? [created] : [{ ...media, id: Date.now().toString() }]);
      } else if (mode === "bulk") {
        const results = [];
        for (const mediaFile of mediaData) {
          const { file, dimensions, name } = mediaFile;
          try {
            const response = await uploadToS3Storage(file, file.type);
            results.push({
              type: mediaTypeValue,
              key: response.key,
              url: response.url,
              width: dimensions.width,
              height: dimensions.height,
              name,
              size: file.size,
              fileType: file.type.split("/")[1].toUpperCase(),
            });
          } catch (uploadError) {
            console.error(`Failed to upload ${file.name}:`, uploadError);
          }
        }
        await bulkCreateOrgMedia(results, []);
        let refetched;
        if (helpers.isVideoAds) {
          refetched = await refetchVideoMedias();
        } else {
          refetched = await refetchMedias();
        }
        // Match newly created items by key to get real IDs
        const uploadedKeys = new Set(results.map((r) => r.key));
        const refetchedMedias = refetched?.data?.medias || [];
        const matched = refetchedMedias.filter((m) => uploadedKeys.has(m.key));
        setUploadedMedia(matched.length > 0 ? matched : results.map((r, i) => ({ ...r, id: `bulk-${i}` })));
      }
    } catch (error) {
      message.error(`Upload failed: ${error.message || "Unknown error"}`);
    } finally {
      setUploadLoading(false);
    }
  };

  // Close upload modal and clear success state
  const handleUploadModalClose = () => {
    setUploadModalVisible(false);
    setUploadedMedia(null);
  };

  // Post-upload: user chose "Assign to Ad Group"
  const handlePostUploadAssign = (createdMedia) => {
    const ids = createdMedia.map((m) => m.id).filter(Boolean);
    setUploadModalVisible(false);
    setUploadedMedia(null);
    if (ids.length > 0) {
      setSelectedIds(ids);
    }
    setAssignModalVisible(true);
  };

  // Post-upload: user chose "Create New Ad Group"
  const handlePostUploadCreateGroup = (createdMedia) => {
    const ids = createdMedia.map((m) => m.id).filter(Boolean);
    setUploadModalVisible(false);
    setUploadedMedia(null);
    if (ids.length > 0) {
      setSelectedIds(ids);
      setAdGroupMediasState(ids.map((id) => ({ id })));
    }
    setCreateGroupVisible(true);
  };

  // Bulk action handlers
  const handleBulkDelete = () => {
    if (helpers.isAdUnits) {
      onDeleteMedias(selectedIds);
    } else {
      onDeleteAdgroup(selectedIds);
    }
  };

  const handleBulkCreateGroup = () => {
    setAdGroupMediasState(selectedIds.map((id) => ({ id })));
    setCreateGroupVisible(true);
  };

  const validateCreateGroupName = (_, value) => {
    if (!value) return Promise.resolve();
    const lowerValue = value.toLowerCase().trim();
    const sameTypeGroups = (adGroupCreatives || []).filter((group) => {
      if (!Array.isArray(group.medias) || group.medias.length === 0) return false;
      if (helpers.isDisplayAds) {
        return !group.medias.some((m) => m.fileType === "MP4");
      }
      return group.medias.some((m) => m.fileType === "MP4");
    });
    const isDuplicate = sameTypeGroups.some(
      (group) => group.name?.toLowerCase().trim() === lowerValue
    );
    if (isDuplicate) {
      return Promise.reject(new Error("Ad group name already exists"));
    }
    return Promise.resolve();
  };

  const handleCreateGroupSubmit = async () => {
    if (creatingGroup) return;
    try {
      setCreatingGroup(true);
      const values = await createGroupForm.validateFields();
      await onCreateAdgroup(values.name, adGroupMediasState);
      createGroupForm.resetFields();
      setCreateGroupVisible(false);
    } catch (error) {
      if (error?.errorFields) return; // form validation error, not a submit error
      message.error("Failed to create ad group");
    } finally {
      setCreatingGroup(false);
    }
  };

  const handleBulkDownload = async () => {
    if (bulkDownloading) return;
    setBulkDownloading(true);
    try {
      if (helpers.isAdUnits) {
        // Units tab — flat zip
        const keys = selectedUnits.map((u) => u.key).filter(Boolean);
        if (!keys.length) {
          message.warning("No creatives to download");
          return;
        }
        message.loading("Downloading...", 0);
        const results = await multiImageZipKey("creatives", keys);
        message.destroy();
        if (results.success === false) {
          message.error("Failed to download creatives");
        } else {
          message.success(`Successfully downloaded ${results.successfulDownloads || keys.length} creative(s)`);
        }
      } else {
        // Groups tab — one subfolder per ad group
        const selectedGroups = rawGroups.filter((g) => selectedIds.includes(g.id));
        const groups = selectedGroups
          .map((g) => ({
            name: g.name,
            keys: (g.medias || []).map((m) => m.key).filter(Boolean),
          }))
          .filter((g) => g.keys.length > 0);
        if (!groups.length) {
          message.warning("No creatives to download");
          return;
        }
        message.loading("Downloading...", 0);
        const results = await multiGroupZipKey("ad-groups", groups);
        message.destroy();
        if (results.success === false) {
          message.error("Failed to download creatives");
        } else {
          message.success(`Successfully downloaded ${results.successfulDownloads} creative(s) from ${groups.length} group(s)`);
        }
      }
    } catch {
      message.destroy();
      message.error("Failed to download creatives");
    } finally {
      setBulkDownloading(false);
    }
  };

  const handleBulkAssign = () => {
    setAssignModalVisible(true);
  };

  const handleAssignSubmit = () => {
    handleAssignAdUnits(selectedAdGroupIds, selectedIds);
    setAssignModalVisible(false);
    setSelectedAdGroupIds([]);
    setSelectedIds([]);
  };

  // Card / row click — navigate to detail
  const handleItemClick = (item) => {
    if (helpers.isAdUnits) {
      handleRowClick(item);
    } else {
      // Group detail navigation via router
      const mediaType = helpers.isVideoAds ? "VIDEO" : "DISPLAY";
      navigateToDetail("adgroup", item.id, mediaType);
    }
  };

  // Group actions dropdown handler (desktop table)
  const handleGroupAction = async (actionKey, record) => {
    const mediaType = helpers.isVideoAds ? "VIDEO" : "DISPLAY";
    switch (actionKey) {
      case "amplify":
        try {
          await setAdGroupAsAmplifyDefault(record);
          message.success("Set as Amplify Default");
        } catch {
          message.error("Failed to set Amplify Default");
        }
        break;
      case "edit":
        setSelectedGroupRow(record);
        setEditGroupVisible(true);
        break;
      case "addCreatives":
        setSelectedGroupRow(record);
        setAddCreativesVisible(true);
        break;
      case "download":
        try {
          const keys = (record.medias || []).map((m) => m.key).filter(Boolean);
          if (!keys.length) {
            message.warning("No creatives to download");
            return;
          }
          const filename = (record.name || "creatives")
            .replace(/[^a-z0-9]/gi, "_")
            .toLowerCase();
          message.loading("Downloading...", 0);
          const results = await multiImageZipKey(filename, keys);
          message.destroy();
          if (results.success === false) {
            message.error("Failed to download creatives");
          } else {
            const successCount =
              results.successfulDownloads || keys.length;
            message.success(
              `Successfully downloaded ${successCount} creative(s)`
            );
          }
        } catch {
          message.destroy();
          message.error("Failed to download creatives");
        }
        break;
      default:
        break;
    }
  };

  // ── Tab definitions ─────────────────────────────────────────────────────
  const tabItems = [
    { key: "displayUnits", label: "Display Units" },
    { key: "videoUnits", label: "Video Units" },
    { key: "displayGroups", label: "Display Groups" },
    { key: "videoGroups", label: "Video Groups" },
  ];

  // ── Render guards ───────────────────────────────────────────────────────
  if (!isRouteValid) {
    return (
      <div className="creative-library__loading">
        <AppSpinner size="large" />
      </div>
    );
  }

  if (!loggedInOrg || !getAdGroupsByMedia || !deleteAdGroupCreatives) {
    return (
      <div className="creative-library__loading">
        <AppSpinner size="large" />
      </div>
    );
  }

  const isLoading = assigning || onLoadingDeleteMedias || onLoadingDeleteAdGroup;

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <AppSpinner spinning={isLoading} tip="Loading..." size="large">
      <div className="creative-library">
        {/* Header */}
        <div className="creative-library__header">
          <h1 className="creative-library__title">
            <AppstoreAddOutlined className="creative-library__title-icon" />
            Creatives
          </h1>
        </div>

        {/* Tabs + toolbar */}
        <div className="creative-library__tabs">
          {isMobile ? (
            <>
              <Select
                value={currentState.tab}
                onChange={handleTabChange}
                className="creative-library__tab-select"
                suffixIcon={<DownOutlined />}
                options={tabItems.map((t) => ({ value: t.key, label: t.label }))}
              />
              <CreativeToolbar
                searchText={searchText}
                onSearch={setSearchText}
                sortOption={sortOption}
                sortDirection={sortDirection}
                onSortChange={setSortOption}
                onDirectionChange={setSortDirection}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onUpload={() => setUploadModalVisible(true)}
                showUpload={helpers.isAdUnits}
                hideSort={isDesktop}
                hideViewToggle={isDesktop}
              />
            </>
          ) : (
            <Tabs
              activeKey={currentState.tab}
              onChange={handleTabChange}
              items={tabItems}
              moreIcon={null}
              tabBarExtraContent={
                <CreativeToolbar
                  searchText={searchText}
                  onSearch={setSearchText}
                  sortOption={sortOption}
                  sortDirection={sortDirection}
                  onSortChange={setSortOption}
                  onDirectionChange={setSortDirection}
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  onUpload={() => setUploadModalVisible(true)}
                  showUpload={helpers.isAdUnits}
                  hideSort={isDesktop}
                  hideViewToggle={isDesktop}
                />
              }
            />
          )}
        </div>

        {/* Selection alert — inline between tabs and content */}
        <SelectionAlertRow
          selectedCount={selectedIds.length}
          onDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onAssign={handleBulkAssign}
          onCreateGroup={handleBulkCreateGroup}
          onClear={handleClearSelection}
          showAssign={helpers.isAdUnits}
          showCreateGroup={helpers.isAdUnits}
          showDownload
          downloading={bulkDownloading}
        />

        {/* Content */}
        <div className="creative-library__content">
          {isDesktop ? (
            /* ─── Desktop: AntD Table ────────────────────────────────── */
            <CreativeTable
              items={paginatedItems}
              isAdUnits={helpers.isAdUnits}
              isVideo={helpers.isVideoAds}
              selectedIds={selectedIds}
              setSelectedIds={setSelectedIds}
              onClick={handleItemClick}
              loading={isDataLoading}
              pageNumber={pageNumber}
              pageSize={pageSize}
              total={totalItems}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              updateOrgMedia={updateOrgMedia}
              amplify={amplify}
              onGroupAction={handleGroupAction}
              allMedias={rawUnits}
            />
          ) : (
            /* ─── Mobile/Tablet: Cards or List rows ──────────────────── */
            <>
              {isDataLoading ? (
                <SkeletonGrid viewMode={viewMode} count={viewMode === "grid" ? 8 : 5} />
              ) : paginatedItems.length === 0 ? (
                <div className="creative-library__empty">
                  <Empty
                    description={
                      searchText
                        ? "No results match your search"
                        : "No items yet. Upload your first creative!"
                    }
                  />
                </div>
              ) : viewMode === "grid" ? (
                <div className="creative-library__grid">
                  {paginatedItems.map((item) =>
                    helpers.isAdUnits ? (
                      <CreativeGridCard
                        key={item.id}
                        media={item}
                        selected={selectedIds.includes(item.id)}
                        onSelect={handleToggleSelect}
                        onClick={handleItemClick}
                        isVideo={helpers.isVideoAds}
                      />
                    ) : (
                      <GroupGridCard
                        key={item.id}
                        group={item}
                        selected={selectedIds.includes(item.id)}
                        onSelect={handleToggleSelect}
                        onClick={handleItemClick}
                      />
                    )
                  )}
                </div>
              ) : (
                <div className="creative-library__list">
                  {paginatedItems.map((item) =>
                    helpers.isAdUnits ? (
                      <CreativeListRow
                        key={item.id}
                        media={item}
                        selected={selectedIds.includes(item.id)}
                        onSelect={handleToggleSelect}
                        onClick={handleItemClick}
                        isVideo={helpers.isVideoAds}
                      />
                    ) : (
                      <GroupListRow
                        key={item.id}
                        group={item}
                        selected={selectedIds.includes(item.id)}
                        onSelect={handleToggleSelect}
                        onClick={handleItemClick}
                      />
                    )
                  )}
                </div>
              )}

              {/* Mobile/tablet pagination */}
              {totalItems > 0 && (
                <CreativePagination
                  current={pageNumber}
                  pageSize={pageSize}
                  total={totalItems}
                  onChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  compact
                />
              )}
            </>
          )}
        </div>

        {/* Upload modal */}
        <MediaUploadModal
          visible={uploadModalVisible}
          onCancel={handleUploadModalClose}
          onUpload={handleMediaUpload}
          onAssignToGroup={handlePostUploadAssign}
          onCreateGroup={handlePostUploadCreateGroup}
          mediaType={helpers.isVideoAds ? "VIDEO" : "IMAGE"}
          title={helpers.isVideoAds ? "Upload Video(s)" : "Upload Image(s)"}
          loading={uploadLoading}
          existingCreatives={rawUnits}
          uploadedMedia={uploadedMedia}
        />

        {/* Assignment modal */}
        <AdUnitAssignmentModal
          open={assignModalVisible}
          onCancel={() => setAssignModalVisible(false)}
          selectedAdUnits={selectedUnits}
          availableAdGroups={rawGroups}
          selectedAdGroupIds={selectedAdGroupIds}
          setSelectedAdGroupIds={setSelectedAdGroupIds}
          onAssign={handleAssignSubmit}
          assigning={assigning}
          mediaType={helpers.isVideoAds ? "VIDEO" : "IMAGE"}
        />

        {/* Create ad group modal */}
        <Modal
          title={`Create ${helpers.isVideoAds ? "Video" : "Display"} Ad Group \u2014 ${selectedIds.length} Creative(s) Selected`}
          open={createGroupVisible}
          centered
          footer={null}
          closable={false}
          className="creative-create-group-modal"
          onCancel={() => {
            setCreateGroupVisible(false);
            createGroupForm.resetFields();
          }}
        >
          <Form
            layout="vertical"
            form={createGroupForm}
            onFinish={handleCreateGroupSubmit}
          >
            <Form.Item
              label="Ad Group Name (Max 255 characters)"
              name="name"
              rules={[
                { required: true, message: "Please enter an ad group name" },
                { max: 255, message: "Ad group name must not exceed 255 characters" },
                { validator: validateCreateGroupName },
              ]}
            >
              <Input placeholder="Name of Ad Group" maxLength={255} showCount />
            </Form.Item>
            <div className="creative-create-group-modal__footer">
              <Button
                disabled={creatingGroup}
                onClick={() => {
                  setCreateGroupVisible(false);
                  createGroupForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={creatingGroup}
                disabled={creatingGroup}
              >
                Create
              </Button>
            </div>
          </Form>
        </Modal>

        {/* Edit ad group modal (rename / remove creatives) */}
        {editGroupVisible && selectedGroupRow && (
          <CreativeAdGroupForm
            visible={editGroupVisible}
            setVisible={setEditGroupVisible}
            selectedAdGroup={selectedGroupRow}
            updateAdGroup={updateAdGroup}
            allAdGroups={rawGroups}
          />
        )}

        {/* Add creatives to ad group modal */}
        {addCreativesVisible && selectedGroupRow && (
          <CreativeAdGroupAddCreativesModal
            visible={addCreativesVisible}
            setVisible={setAddCreativesVisible}
            selectedAdGroup={selectedGroupRow}
            orgMedias={rawUnits}
            setCreativeKeys={setGroupCreativeKeys}
            updateAdGroup={updateAdGroup}
          />
        )}
      </div>
    </AppSpinner>
  );
};

export default LibraryCreativesView;
